import { useCallback, useEffect, useId, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ModalLoadingState } from "../modals/ModalLoadingState";
import { ModalShell } from "../modals/ModalShell";
import { ModalFootVal, ModalValHint } from "../modals/ModalValHint";
import { useGatedAction } from "../modals/useGatedAction";
import { useDevice } from "../../context/DeviceProvider";
import { useModal } from "../../context/ModalProvider";
import { useAuth } from "../../context/AuthProvider";
import type { CreateEventInput, CreateEventOutcome, EventDTO } from "../../lib/eventsApi";
import { addDaysYmd, compareYmd, formatYmdLong, localTodayYmd } from "../../lib/dates";
import { clampCarbTarget } from "../../lib/fuelClamps";
import { type EventSearchFieldHandle } from "../Onboarding/EventSearchField";
import { LucideIcon } from "../FG/LucideIcon";
import { useJourneys } from "../JourneyRail/useJourneys";
import { patchFuelingProfile, type FuelingProfilePatch } from "../../lib/fuelingProfileApi";
import { useAboutYouFuel } from "./profileSteps/useAboutYouFuel";
import { BodyStep } from "./profileSteps/BodyStep";
import { BoxCadenceStep } from "./profileSteps/BoxCadenceStep";
import { FuelExperienceStep } from "./profileSteps/FuelExperienceStep";
import { NeedsTastesStep } from "./profileSteps/NeedsTastesStep";
import { weightToKg } from "../../lib/units";
import {
  buildReviewRows,
  computeIntroSummary,
  summarizePlannedWorkouts,
  type PlannedSummary
} from "./rollingReview";
import { listWorkouts } from "../../lib/plannerApi";
import { evaluateCollision } from "@shared/modules/journeys/collisionGeometry";
import { journeyRaceName, type EventRef } from "../../lib/journeyApi";
import {
  collectUpcomingRaces,
  sameRaceIdentity,
  toWizardRace,
  type RaceSource,
  type WizardRace
} from "../../lib/raceSources";
import { RoleResolutionSheet } from "./RoleResolutionSheet";
import { defaultJourneyWindow, journeyCoveringDate } from "../../lib/journeyWindow";
import { useEventSetup } from "./hooks/useEventSetup";
import { useRoleResolution } from "./hooks/useRoleResolution";
import { useToast } from "../../context/ToastProvider";
import { useConnectorOffer } from "../../hooks/useConnectorsManifest";
import { AddEventModal } from "../modals/AddEventModal";
import {
  catalogEventStartToYmd,
  eventDistanceLabel,
  formatEventLocation,
  type CatalogDistanceOption,
  type CreateCustomEventInput
} from "../../lib/eventsApi";
import {
  getTrainingPlanLibrary,
  resolveLibraryDistanceId,
  resolveTrainingPlan,
  type PaceBand,
  type ResolvePlanResult,
  type TrainingPlanLibrary
} from "../../lib/trainingPlanApi";
import {
  autoFormatFinishTime,
  finalizeFinishTimeInput,
  isPartialFinishTimeFormat,
  nearestPlan,
  parseFinishMinutes,
  roundDownToPlanFinish,
  sanitizeFinishTimeInput,
  slowestPlanFinish,
  type GoalMode
} from "./wizard/finishTime";
import { formatYmdShort } from "./wizard/formatting";
import {
  STEP_FOOTNOTES,
  STEP_META,
  wizardStepOrder,
  type WizardStepId
} from "./wizard/wizardMeta";
import { GoalStep } from "./wizard/steps/GoalStep";
import { IntentStep } from "./wizard/steps/IntentStep";
import {
  MethodStep,
  trainingChoiceOf,
  type WizardTrainingCard,
  type WizardTrainingChoice
} from "./wizard/steps/MethodStep";
import { PlanLengthStep } from "./wizard/steps/PlanLengthStep";
import { RaceStep } from "./wizard/steps/RaceStep";
import { ReviewStep } from "./wizard/steps/ReviewStep";
import { useOnboardingAnalytics } from "./wizard/useOnboardingAnalytics";
import type { OnboardingEntry } from "../../lib/onboardingAnalytics";

/**
 * The onboarding wizard (PB-3, architecture C / D-16: one decision per screen
 * on the shared `ModalShell`). Q1 "Do you have a race coming up?" opens one of
 * two paths:
 *   - race: Race → Training method → Goal (DB pace bands only) → Plan length
 *     (DB lengths + phase bars + honest start-partway; Goal + length only for a
 *     Fuel Goods plan) → profile steps → Review → `onApply`. Reads the DB-backed
 *     library (`/api/training-plans`) and maps a goal finish time + plan length
 *     to a stored plan id; the apply (insert block + race event) is delegated to
 *     `onApply` so the screen owns the calendar refresh + "Remove plan" toast.
 *   - no race: Training method → profile steps → Box cadence + coverage →
 *     Review → creates a rolling journey and hands it to `onRollingCreated`.
 * The wizard never decides where the athlete lands: both finishes hand the
 * host an outcome and the host (`useWizardFinish`) runs the training-method
 * handoff + the landing rule.
 * Profile steps = Fueling experience → About your body → Needs & tastes → Box cadence.
 */

/**
 * What the wizard hands the apply handler. ATHLETE-state travels as a
 * `fuelingProfile` patch written FIRST (profile-first, fueling-profile plan
 * R4 — the profile is user-level data, so persisting it is not a failure even
 * if the journey create then fails); the journey body keeps only event-scoped
 * fueling (cadence + race-day carb target).
 */
export type WizardApplyBody = {
  /** Absent for a plan-less "build your own" race journey (build method / no plans). */
  planId?: string;
  /**
   * The training method the athlete chose on the Method step. "build" (or a
   * no-plan distance) sends no planId, so a plan-less race journey is created.
   * Lets the apply distinguish an intentional build from a Fuel Goods plan
   * apply rather than inferring from planId (import, Phase 2, is plan-less too).
   */
  method?: "fuel_goods_plan" | "build";
  /**
   * The athlete's answer to "Do you have a training plan?" (D17). Every answer
   * but the Fuel Goods plan creates a plan-less journey; the host then runs
   * that answer's handoff (connect / import / generate) — see `useWizardFinish`.
   */
  trainingChoice?: WizardTrainingChoice;
  /** Snapshot at apply time — only meaningful when `trainingChoice` is connector. */
  connectorLinked?: boolean;
  /** Provider labels on offer at apply time (for the post-race connect modal). */
  connectorOfferLabels?: string[];
  /** Upcoming planned workouts as the wizard knows them (host prop or its own
   *  lookup) — drives the host's landing rule (My Fuel vs the calendar). */
  hasUpcomingWorkouts?: boolean;
  raceDate: string;
  raceName?: string;
  eventCatalogId?: string;
  distanceLabel?: string;
  customerEventId?: string;
  planTypeLabel?: string;
  dropWeeks?: number;
  customTargets?: { raceDayGramsPerHour?: number };
  fuelingProfile?: FuelingProfilePatch;
  /**
   * Journey window start (J2) from the step-2 selection — the library plan's
   * effective start (clamped to today for mid-plan applies) or the custom
   * window for no-plan distances. Rides the journey create as `startYmd`.
   */
  startYmd?: string;
  /**
   * B2 (robustness spec §5.5): the wizard PRE-CHECK collision sheet's answer
   * (chain → "chain"; nest_existing → "demote"), carried onto the create so
   * the server resolves the overlap in one shot instead of 409ing and
   * re-asking the exact same question after the user already answered it.
   */
  overlapResolution?: { mode: "chain" | "demote" };
};

/**
 * What a finished wizard hands its host (D5/D6): the host runs the chosen
 * method's handoff, then lands on My Fuel when workouts exist, else the
 * calendar. The rolling finish passes this to `onRollingCreated`; the race
 * finish's host builds the same shape from the apply body + created journey.
 */
export type WizardFinishOutcome = {
  kind: "race" | "rolling";
  /** The created journey; null when a race was nested into an existing build
   *  as a prep race (no journey of its own). */
  journey: {
    id: string;
    planGroupId: string | null;
    raceName: string | null;
    raceYmd: string | null;
    /** Window start / rolling anchor (the server may anchor after today). */
    startYmd: string | null;
  } | null;
  trainingChoice?: WizardTrainingChoice;
  connectorLinked?: boolean;
  connectorOfferLabels?: string[];
  hasUpcomingWorkouts: boolean;
};

/** Offered initial-coverage chips (no-race path). Kept in sync with the
 *  radio row below — the coverage default rounds UP to one of these. */
const COVERAGE_CHOICES = [4, 6, 8, 12] as const;

/** Continue nudge while the plan library failed to load (Retry is in the callout). */
const LIBRARY_ERROR_NUDGE = "Couldn't load plans — retry above.";

/**
 * Default the no-race path's "How far ahead should we plan?" from the
 * athlete's actual planned calendar span rather than a flat constant (issue
 * #12): round the planned span UP to the nearest offered chip, floored at the
 * smallest (4). When the span is unknown or there are no planned workouts yet
 * (`undefined`/`0`), keep the historical default of 8.
 */
export function defaultCoverageWeeks(plannedSpanWeeks?: number): number {
  if (!plannedSpanWeeks || plannedSpanWeeks <= 0) return 8;
  const rounded = COVERAGE_CHOICES.find((w) => w >= plannedSpanWeeks);
  return rounded ?? COVERAGE_CHOICES[COVERAGE_CHOICES.length - 1];
}

/** Race-step autofill: preselect only when there is exactly one upcoming race. */
export function soleUpcomingRace<T>(races: readonly T[]): T | null {
  return races.length === 1 ? races[0]! : null;
}

export function TrainingPlanWizard({
  onClose,
  onBack,
  onApply,
  savedEvents,
  createEvent,
  plannedSpanWeeks,
  plannedSummary,
  onRollingCreated,
  initialMethod,
  initialRace,
  goalEdit = false,
  initialGoal,
  initialWeeks,
  importOffered = false,
  hasUpcomingWorkouts,
  entry = "other"
}: {
  onClose: () => void;
  /** Renders a "← Back" chevron (returns to the hub). */
  onBack?: () => void;
  /** Performs the apply (POST + refresh + toast). Resolves on success. Return
   *  `false` to signal the apply was CANCELLED partway (e.g. the user dismissed
   *  a nested overlap-resolution sheet) — the wizard then stays open instead of
   *  closing onto whatever launched it. Any other resolution closes the wizard. */
  onApply: (body: WizardApplyBody) => Promise<void | boolean>;
  /**
   * The no-race finish: fired the instant `createRollingJourney` succeeds
   * (before `onClose`). The host owns the toast, the method handoff and where
   * the athlete lands — the wizard never navigates on a finish. Never fires on
   * a race finish or a dismiss.
   */
  onRollingCreated: (outcome: WizardFinishOutcome) => void;
  /**
   * No-race path: the athlete's planned calendar span in weeks (from the
   * workbench's known workouts). Seeds the "How far ahead should we plan?"
   * coverage default via `defaultCoverageWeeks` — rounded UP to the nearest
   * offered chip. Undefined/0 (no workouts, or a host without a workbench)
   * keeps the historical default of 8. The control stays fully adjustable.
   */
  plannedSpanWeeks?: number;
  /**
   * No-race path: a summary of the athlete's planned calendar (session count,
   * upcoming date range, workouts missing a duration). Drives the review's
   * TRAINING row + its guardrail-5 warnings. Undefined = "no sessions known".
   */
  plannedSummary?: PlannedSummary;
  /**
   * D18 — the athlete already has upcoming planned workouts, so a fresh
   * (Q1-first) wizard skips "Do you have a training plan?" on both paths and
   * finishes as build. Ignored when a race is preloaded (swap method / goal
   * edit open AT that question). Undefined + no `plannedSummary` = the wizard
   * looks the calendar up itself.
   */
  hasUpcomingWorkouts?: boolean;
  /**
   * Pre-selects the training-plan answer. Defaults to "plan" (recommended);
   * the preloaded-race doors pass "plan". The no-race path never offers the
   * plan, so it starts with nothing selected there.
   */
  initialMethod?: WizardTrainingChoice;
  /**
   * Preloads the wizard with a race the caller already picked (the light funnel's
   * "Build me a training plan" opt-in, or "Swap method" from an existing journey),
   * so it opens at the Method step instead of the race search. Exactly one of
   * `customerEventId` (an existing profile event) / `eventCatalogId` (an anon
   * catalog link) is typically set; `distanceLabel` should be present so the
   * distance-sync effect loads the right plan library (else it defaults to
   * marathon). A past `dateYmd` is still caught by the existing `raceInPast` guard.
   */
  initialRace?: {
    name: string;
    dateYmd: string;
    customerEventId?: string;
    eventCatalogId?: string;
    distanceLabel?: string;
    location?: string;
    source?: RaceSource;
  };
  /**
   * Goal-time edit (settings "Edit" beside Goal time): requires `initialRace`;
   * opens straight at the GOAL step (method preset to "plan") with a callout
   * that changing the goal re-applies the plan's workouts at the new paces.
   * The finish flows into the same apply → the server's archive-and-replace
   * swaps upcoming sessions only (past weeks and fueled boxes stay).
   */
  goalEdit?: boolean;
  /** Pre-selects the goal preset (the journey's current `goalFinish`) so the
   *  goal-edit open shows where the athlete is today. A value the library
   *  doesn't offer falls back to the default (middle) band. */
  initialGoal?: string | null;
  /** Pre-selects the plan length (the journey's current `planWeeks`) on the
   *  goal-edit open. A length the library no longer offers falls back to the
   *  default. */
  initialWeeks?: number | null;
  /**
   * The screen's already-fetched event list (snapshot at modal open). Passed
   * down so the wizard doesn't fire a second `/api/events` fetch; the wizard
   * only reads it for the initial "Your races" suggestions — a race created
   * inside the wizard is used via the create return value, not this list.
   */
  savedEvents: EventDTO[];
  /** The screen's event-create (it refetches the parent list on success). */
  createEvent: (input: CreateEventInput) => Promise<CreateEventOutcome>;
  /** Shows the "Import my coach's plan" card (import flag on). Picking it
   *  finishes as a plan-less journey and the host opens the import review
   *  attached to it (`trainingChoice: "import"`). */
  importOffered?: boolean;
  /** The surface that opened the wizard — funnel analytics only. */
  entry?: OnboardingEntry;
}) {
  const device = useDevice();
  const isMobile = device === "mobile";
  const { offered: connectorOffered, connected: connectorConnected } = useConnectorOffer();
  const raceFieldId = useId();
  const raceSearchRef = useRef<EventSearchFieldHandle>(null);
  const focusRaceSearchRef = useRef(false);
  /** Per-distance library cache — makes distance-chip toggles instant on revisit. */
  const libraryCacheRef = useRef<Map<string, TrainingPlanLibrary>>(new Map());
  const today = localTodayYmd();
  const { open: openModal, close: closeModal } = useModal();
  const { state: authState, refreshCsrf } = useAuth();
  const isCustomer = authState.kind === "customer";
  const csrfToken =
    authState.kind === "customer" || authState.kind === "anon" ? authState.csrfToken : null;

  const [distance, setDistance] = useState("marathon");
  const [library, setLibrary] = useState<TrainingPlanLibrary | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Bumped by the error callout's Retry — re-runs the library fetch below.
  const [libraryAttempt, setLibraryAttempt] = useState(0);
  // A preloaded race (the funnel opt-in / "Swap method") starts at the Method
  // step with the race already selected; a goal edit jumps straight to the
  // Goal step (method is preset "plan"); otherwise Q1 (D-16).
  const [step, setStep] = useState<WizardStepId>(() =>
    initialRace ? (goalEdit ? "goal" : "method") : "q1"
  );
  // Q1's answer: null until answered; a preloaded race already answered it.
  // `false` = the no-race path (method → profile → a rolling journey).
  const [hasRace, setHasRace] = useState<boolean | null>(initialRace ? true : null);
  const noRace = hasRace === false;
  // The race the user searched-and-selected (catalog) or added to the DB. The
  // date always comes from the event — there is no free-typed race and no
  // standalone date field. Exactly one of eventCatalogId / customerEventId is
  // set (catalog match vs a just-created custom upcoming event).
  const [query, setQuery] = useState("");
  // Seeded from `initialRace` when the caller preloaded one (the funnel opt-in /
  // "Swap method") — the shapes match, so it maps straight to a `WizardRace`. The
  // Race-step autofill effect is disarmed below so it can't clobber this.
  const [selectedRace, setSelectedRace] = useState<WizardRace | null>(
    initialRace
      ? {
          name: initialRace.name,
          dateYmd: initialRace.dateYmd,
          location: initialRace.location,
          eventCatalogId: initialRace.eventCatalogId,
          distanceLabel: initialRace.distanceLabel,
          customerEventId: initialRace.customerEventId,
          source: initialRace.source
        }
      : null
  );
  // B2 (robustness spec §5.5): the pre-check collision sheet's chain/split/
  // demote answer, held until apply so the create carries `overlapResolution`
  // and the server never re-asks the question the user already answered.
  // Cleared whenever the selected race changes (the answer was race-specific).
  const [pendingOverlapMode, setPendingOverlapMode] = useState<
    "chain" | "demote" | null
  >(null);
  const selectedRaceKey = selectedRace ? `${selectedRace.name}|${selectedRace.dateYmd}` : null;
  useEffect(() => {
    setPendingOverlapMode(null);
  }, [selectedRaceKey]);
  // "Do you have a training plan?" (D17) — the tapped card; `trainingChoice`
  // below derives the answer. Signed-in defaults to the recommended plan;
  // guests start with nothing selected (Connect is disabled for them). A
  // distance we don't stock (or no race) forces build once they pick plan.
  const [trainingCard, setTrainingCard] = useState<WizardTrainingCard | null>(() => {
    if (initialMethod === "connector" && !isCustomer) return null;
    if (initialMethod) return initialMethod;
    return isCustomer ? "plan" : null;
  });
  // Distance options for the picked catalog event — shown inline below the race
  // (in the same modal) when the event offers more than one distance.
  const [raceDistanceChoices, setRaceDistanceChoices] = useState<CatalogDistanceOption[]>([]);
  const [finish, setFinish] = useState<string | null>(initialGoal ?? null);
  const [goalMode, setGoalMode] = useState<GoalMode>("preset");
  const [customFinishInput, setCustomFinishInput] = useState("");
  const customFinishInputRef = useRef<HTMLInputElement>(null);
  // Server-resolved exact/off-grid plan for a manual "custom" goal time. Only
  // the 15-min grid ships in the library; an exact by-minute time is resolved
  // via POST /resolve (debounced) so we can apply the precise plan + show its
  // paces. null unless in custom mode with a matched time.
  const [resolved, setResolved] = useState<ResolvePlanResult | null>(null);
  const finishBeforeCustomRef = useRef<string | null>(null);
  const [weeks, setWeeks] = useState<number | null>(null);
  const [applying, setApplying] = useState(false);
  // F-03-3: synchronous in-flight lock. `applying` (state) drives the disabled
  // button UI but is read from the render closure, so two taps dispatched before
  // React re-renders both see `applying === false` and both fire — creating
  // duplicate events/journeys/workouts. This ref flips synchronously, so the
  // second tap is rejected immediately. Released together with `setApplying(false)`
  // on every non-navigating exit (error / overlap-cancel).
  const submitLockRef = useRef(false);
  const releaseSubmit = () => {
    submitLockRef.current = false;
    setApplying(false);
  };
  const [applyError, setApplyError] = useState<string | null>(null);
  // True after the user dismissed the overlap-resolution sheet without picking:
  // the apply can't finish until they resolve it, so we surface a hint by the
  // Apply button (cleared on the next apply attempt or when the race changes).
  const [overlapUnresolved, setOverlapUnresolved] = useState(false);
  // Active race plans — drive the same-event replace confirm + overlap warning
  // at apply time (event-scoped-race-plans B3) and the Race-step milestone
  // short-circuit (journeys overhaul P2, J4).
  const { journeys: activeRacePlans, suppressedGoalEventIds } = useJourneys();
  const { setupEvent, createRollingJourney } = useEventSetup();
  const { reclassify } = useRoleResolution();
  const { push: pushToast } = useToast();
  const navigate = useNavigate();
  // The apply/save mutations aren't abortable, so if the modal is closed while
  // one is in flight, its resolution must not setState on the unmounted wizard.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Returning-athlete shortcut (Race-step banner): the soonest race journey that
  // already has workouts on the calendar (an applied plan's group, or generated
  // fuel boxes). Lets someone who's already set up jump straight to their
  // calendar instead of re-running the wizard.
  const existingJourneyWithWorkouts = useMemo(
    () =>
      activeRacePlans
        .filter(
          (j) =>
            j.journeyType !== "rolling" &&
            j.raceYmd != null &&
            (j.planGroupId != null || j.drops.length > 0)
        )
        .sort((a, b) => compareYmd(a.raceYmd ?? "9999-12-31", b.raceYmd ?? "9999-12-31"))[0] ??
      null,
    [activeRacePlans]
  );
  // Drop cadence (event/journey-scoped, NOT athlete-state) — how the fuel boxes
  // split. Stays in the wizard; the athlete profile + fueling fields live in the
  // shared `useAboutYouFuel` hook (profile-steps single-source, plan A), wired
  // in below once `library`/`band`/`goalHours` (its estimate inputs) are ready.
  // D4: "Every 2 weeks · Most popular" starts preselected.
  const [dropWeeks, setDropWeeks] = useState(2);
  // No-race path only: how many weeks forward to tile initially (→ server
  // `coverageWeeks`). Ignored on the race path (a race has an endpoint).
  // Seeded from the athlete's planned calendar span (issue #12) rounded UP to
  // an offered chip; falls back to 8 when unknown. Fully adjustable after —
  // this is a default, not a lock.
  const [coverageWeeks, setCoverageWeeks] = useState(() =>
    defaultCoverageWeeks(plannedSpanWeeks)
  );

  // Fetch the library for the selected distance in the background once the
  // athlete says they have a race — the Race step (race search) renders
  // immediately; later steps gate on `libraryReady`. Q1 and the no-race path
  // need no plans, so nothing is fetched until `hasRace` is true.
  useEffect(() => {
    if (hasRace !== true) return;
    const ctrl = new AbortController();
    // A load seeds the goal + length defaults only where the current pick is
    // unset or not offered by THIS library (a distance switch), so the
    // goal-edit preset (`initialGoal` / `initialWeeks`) and a pick that is
    // still valid survive the first load, a cached re-load and a retry. The
    // custom / just-finish goal modes are distance-specific, so a (re)load —
    // which only happens on a distance change, first open or retry — resets
    // them to the preset grid.
    const applyLibrary = (lib: TrainingPlanLibrary) => {
      setLibrary(lib);
      setLoadError(null);
      const offersFinish = (f: string | null | undefined): f is string =>
        !!f && lib.paceBands.some((b) => b.finish === f);
      const offersWeeks = (w: number | null | undefined): w is number =>
        w != null && lib.lengths.includes(w);
      const mid = lib.paceBands[Math.floor(lib.paceBands.length / 2)];
      setGoalMode("preset");
      setCustomFinishInput("");
      setFinish((prev) =>
        offersFinish(prev) ? prev : offersFinish(initialGoal) ? initialGoal : (mid?.finish ?? null)
      );
      setWeeks((prev) =>
        offersWeeks(prev)
          ? prev
          : offersWeeks(initialWeeks)
            ? initialWeeks
            : (lib.lengths[lib.lengths.length - 1] ?? 16)
      );
    };
    const cached = libraryCacheRef.current.get(distance);
    if (cached) {
      applyLibrary(cached);
      return () => ctrl.abort();
    }

    void getTrainingPlanLibrary(distance, ctrl.signal)
      .then((lib) => {
        if (ctrl.signal.aborted) return;
        libraryCacheRef.current.set(distance, lib);
        applyLibrary(lib);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setLoadError(err instanceof Error ? err.message : "Couldn't load plans");
      });
    return () => ctrl.abort();
  }, [distance, hasRace, libraryAttempt, initialGoal, initialWeeks]);
  const retryLibrary = () => {
    setLoadError(null);
    setLibraryAttempt((n) => n + 1);
  };

  // Search-first race step (Phase 2.1) — the distance comes from the picked
  // race, so keep the plan-library `distance` in sync with the race's label
  // instead of asking for it up front.
  useEffect(() => {
    const label = selectedRace?.distanceLabel;
    if (!label || !library) return;
    // Map the race's free-form distance label to a plan-library id. The resolver
    // is specificity-ordered (canonical map → longest catalog match → custom) so
    // a label can't be swallowed by a substring collision — "Half marathon"
    // resolves to `half`, never `marathon` — and any distance we don't stock a
    // plan catalog for resolves DETERMINISTICALLY to "custom" (the no-plan
    // catch-all) instead of inheriting whichever distance was loaded before.
    const resolved = resolveLibraryDistanceId(label, library.distances);
    if (resolved !== distance) setDistance(resolved);
  }, [selectedRace?.distanceLabel, library, distance]);

  const planFinish = useMemo(() => {
    if (!library?.paceBands.length) return null;
    if (goalMode === "just_finish") return slowestPlanFinish(library.paceBands);
    if (goalMode === "custom") {
      const mins = parseFinishMinutes(customFinishInput);
      if (mins == null) return null;
      return roundDownToPlanFinish(mins, library.paceBands);
    }
    return finish;
  }, [goalMode, finish, customFinishInput, library]);

  const goalLabel = useMemo(() => {
    if (goalMode === "just_finish") return "Just finish";
    if (goalMode === "custom") return customFinishInput.trim() || null;
    return finish;
  }, [goalMode, finish, customFinishInput]);

  const band: PaceBand | null = useMemo(() => {
    // Custom mode: prefer the exact resolved band (may be an off-grid minute).
    if (goalMode === "custom" && resolved?.band) return resolved.band;
    if (!library?.paceBands.length) return null;
    if (planFinish) return library.paceBands.find((b) => b.finish === planFinish) ?? null;
    if (goalMode === "just_finish") return library.paceBands.at(-1) ?? null;
    return null;
  }, [library, planFinish, goalMode, resolved]);
  const plan = useMemo(
    () => (library && planFinish && weeks ? nearestPlan(library.plans, weeks, planFinish) : null),
    [library, planFinish, weeks]
  );

  // Custom goal time → resolve the exact (possibly off-grid) plan server-side,
  // debounced. Non-custom modes clear it. A failed/aborted call leaves `resolved`
  // null so the UI falls back to the nearest listed plan.
  useEffect(() => {
    if (goalMode !== "custom" || !library || !weeks) {
      setResolved(null);
      return;
    }
    const mins = parseFinishMinutes(customFinishInput);
    if (mins == null) {
      setResolved(null);
      return;
    }
    const ctrl = new AbortController();
    const t = window.setTimeout(() => {
      resolveTrainingPlan({ distance, weeks, minutes: mins }, ctrl.signal)
        .then(setResolved)
        .catch(() => {
          /* keep null — nearest-listed fallback applies */
        });
    }, 300);
    return () => {
      window.clearTimeout(t);
      ctrl.abort();
    };
  }, [goalMode, customFinishInput, weeks, distance, library]);

  // The plan id actually applied: the server-resolved exact plan when in custom
  // mode, else the nearest listed plan the preview is built from.
  const effectivePlanId = useMemo(
    () => (goalMode === "custom" && resolved ? resolved.planId : plan?.id),
    [goalMode, resolved, plan]
  );

  // Races the principal already has — customer profile events (anon get [] from
  // the API) plus the anon client-side pending race. Upcoming only, soonest
  // first. Picking one reuses the existing event (the server dedupes on
  // `customerEventId` / catalog + distance) so the profile never gains a copy.
  // One aggregator over events + journeys + anon draft (spec §3.2), so "Your
  // races" answers "what races do I have?" identically to every other surface.
  // Journey-only races (no CustomerEvent) now appear here too — intended.
  const existingRaces = useMemo<WizardRace[]>(
    () =>
      collectUpcomingRaces({
        events: savedEvents,
        journeys: activeRacePlans,
        today,
        isCustomer,
        suppressedGoalEventIds
      }).map(toWizardRace),
    [isCustomer, savedEvents, activeRacePlans, today, suppressedGoalEventIds]
  );

  // The remaining saved races (everything except the one currently selected),
  // surfaced as pinned "Your races" choices at the top of the search dropdown.
  // Exclusion uses the SAME identity rule as the aggregator's dedupe.
  const otherRaces = useMemo(
    () =>
      selectedRace
        ? existingRaces.filter((r) => !sameRaceIdentity(r, selectedRace))
        : existingRaces,
    [existingRaces, selectedRace]
  );

  // Preselect a saved race only when it is the sole upcoming one — with two or
  // more, leave the selector empty so the user picks (pinned "Your races" in
  // search). Runs again when the async event list arrives; the ref keeps it a
  // one-shot so it never clobbers a later edit. A preloaded `initialRace` arms
  // the ref from the first render so the autofill can never clobber the caller's
  // race.
  const didAutofillRef = useRef(!!initialRace);
  useEffect(() => {
    if (didAutofillRef.current) return;
    if (selectedRace || query.trim()) return;
    if (existingRaces.length === 0) return;
    didAutofillRef.current = true;
    const only = soleUpcomingRace(existingRaces);
    if (only) setSelectedRace(only);
  }, [existingRaces, selectedRace, query]);

  useEffect(() => {
    if (selectedRace || !focusRaceSearchRef.current) return;
    focusRaceSearchRef.current = false;
    requestAnimationFrame(() => raceSearchRef.current?.focus());
  }, [selectedRace]);

  const raceDate = selectedRace?.dateYmd ?? null;
  const weeksOut = raceDate
    ? Math.max(0, Math.round((Date.parse(raceDate) - Date.parse(today)) / (7 * 86_400_000)))
    : 0;
  // Runway used for plan-fit math (recommended length, start-partway, fuel
  // weeks). A *selected* race always has at least one week of fit runway, even
  // when it rounds to 0 (a race a few days out): without this floor an imminent
  // race would recommend the LONGEST plan and show no "start partway" note.
  // `weeksOut` itself stays the rounded value used only for the "N weeks out" copy.
  const fitWeeksOut = raceDate ? Math.max(1, weeksOut) : 0;
  // The loaded library actually corresponds to the SELECTED distance. The fetch
  // effect deliberately doesn't clear `library` while the next distance loads, so
  // mid-switch `library` is briefly the PREVIOUS distance's data. Gating on this
  // stops that stale library from answering `noPlansForDistance` (the flash) or
  // driving navigation. The server echoes the requested id back as
  // `library.distance`, so this comparison is exact.
  const libraryReady = !!library && library.distance === distance;
  // Library for the selected distance is loaded and ships zero Fuel Goods plans.
  // Instead of a dead-end, the user gets a custom race journey (auto-sized
  // window) + fueling and adds their own workouts — Goal + Plan-length are
  // skipped (see `stepOrder`).
  const noPlansForDistance = !!selectedRace && libraryReady && library!.plans.length === 0;
  const checkingPlans = !!selectedRace && !libraryReady && !loadError;

  // D18 — upcoming planned workouts. Planner hosts pass `plannedSummary` +
  // `hasUpcomingWorkouts`; hosts with no calendar (landing, storefront embed)
  // pass neither, so a fresh wizard looks the next 12 weeks up once itself.
  // Never blocks Q1: if Q1 is answered first, the training question is asked.
  const hasPrincipal = isCustomer || authState.kind === "anon";
  const selfLoadPlanned =
    plannedSummary === undefined && hasUpcomingWorkouts === undefined && !initialRace && hasPrincipal;
  const [loadedSummary, setLoadedSummary] = useState<PlannedSummary | null>(null);
  useEffect(() => {
    if (!selfLoadPlanned) return;
    const ctrl = new AbortController();
    listWorkouts({ from: today, to: addDaysYmd(today, 12 * 7) }, ctrl.signal)
      .then((res) => {
        if (!ctrl.signal.aborted) setLoadedSummary(summarizePlannedWorkouts(res.workouts, today));
      })
      .catch(() => {
        /* calendar unknown → the training question is simply asked */
      });
    return () => ctrl.abort();
  }, [selfLoadPlanned, today]);
  const summary = plannedSummary ?? loadedSummary ?? undefined;
  // Skip "Do you have a training plan?" only on a fresh (Q1-first) open — a
  // preloaded race (swap method / goal edit) opens AT that question. Live
  // until Q1 is answered, then frozen so a late fetch never reshapes the path.
  const knownUpcoming = hasUpcomingWorkouts ?? (summary?.sessionCount ?? 0) > 0;
  const liveSkipMethod = !initialRace && knownUpcoming;
  const [frozenSkipMethod, setFrozenSkipMethod] = useState<boolean | null>(null);
  const skipMethod = frozenSkipMethod ?? liveSkipMethod;
  // The training-plan answer. The plan card isn't offered with no race or on a
  // distance we don't stock, so the (default) plan pick reads as build there;
  // a skipped question is build (the calendar's workouts are the training).
  // Guests may have no card yet — treat like the signed-in default for step
  // order only until they pick (method advances on tap, so this is provisional).
  const trainingChoice: WizardTrainingChoice = skipMethod
    ? "build"
    : trainingCard == null
      ? noRace || noPlansForDistance
        ? "build"
        : "plan"
      : trainingCard === "plan" && (noRace || noPlansForDistance)
        ? "build"
        : trainingChoiceOf(trainingCard);
  // The single source for "plan-less path" — every answer but the Fuel Goods
  // plan. Drives stepOrder, the apply body (method + no planId) and the recap.
  const isBuild = trainingChoice !== "plan";

  // Multi-distance events: prefetch sibling libraries so chip toggles feel instant.
  useEffect(() => {
    if (!libraryReady || raceDistanceChoices.length <= 1 || !library) return;
    for (const choice of raceDistanceChoices) {
      const id = resolveLibraryDistanceId(choice.label, library.distances);
      if (id === distance || libraryCacheRef.current.has(id)) continue;
      void getTrainingPlanLibrary(id)
        .then((lib) => {
          libraryCacheRef.current.set(id, lib);
        })
        .catch(() => {});
    }
  }, [libraryReady, raceDistanceChoices, library, distance]);

  // Plan-less journey window (build / connector / no-plan distance — there is
  // no length step on this path): the same D2 default the light funnel uses —
  // the longest library length that fits the runway, else start today. A
  // no-plan distance sizes from the static presets (the library's lengths are
  // its plans', which it doesn't have).
  const buildWindowWeeks =
    isBuild && raceDate && libraryReady
      ? defaultJourneyWindow({
          raceYmd: raceDate,
          todayYmd: today,
          libraryLengths: noPlansForDistance ? null : library!.lengths
        }).weeks
      : null;
  const planWeeks = (isBuild ? buildWindowWeeks : weeks) ?? 16;
  const startYmd = raceDate ? addDaysYmd(raceDate, -(planWeeks * 7 - 1)) : null;
  // The journey window start (J2) the apply sends: the step-2 selection's
  // start, clamped to today for mid-plan applies ("starts at week N").
  const journeyStartYmd =
    raceDate && startYmd ? (compareYmd(startYmd, today) >= 0 ? startYmd : today) : null;
  const raceInPast = raceDate ? compareYmd(raceDate, today) < 0 : false;
  // Plan-length step (design step 3): DB lengths only, ascending. The
  // recommended length is the longest that still fits the runway (so the full
  // base phase lands) — else the shortest, which starts closest to on-time.
  const sortedLengths = library ? [...library.lengths].sort((a, b) => a - b) : [];
  const recommendedLen =
    fitWeeksOut > 0
      ? sortedLengths.filter((w) => w <= fitWeeksOut).pop() ??
        sortedLengths[0] ??
        null
      : sortedLengths[sortedLengths.length - 1] ?? null;

  // ── Drops-step projections (design 1e) ─────────────────────────────────
  // Fueled weeks = what's actually left to race (mid-plan applies fuel fewer
  // weeks than the plan holds). Windows chunk from the start; short one last.
  const fuelWeeks = Math.max(1, Math.min(planWeeks, fitWeeksOut || planWeeks));
  const dropWindows = useMemo(() => {
    const out: Array<[number, number]> = [];
    for (let start = 1; start <= fuelWeeks; start += dropWeeks) {
      out.push([start, Math.min(start + dropWeeks - 1, fuelWeeks)]);
    }
    return out;
  }, [fuelWeeks, dropWeeks]);
  const goalHours = useMemo(() => {
    if (goalMode === "custom") {
      const mins = parseFinishMinutes(customFinishInput);
      return mins != null ? mins / 60 : 4;
    }
    if (goalMode === "just_finish") {
      const slowest = library?.paceBands.at(-1);
      return slowest ? slowest.goalMinutes / 60 : 4;
    }
    const m = /^(\d+):(\d{2})/.exec(finish ?? "");
    return m ? Number(m[1]) + Number(m[2]) / 60 : 4;
  }, [finish, goalMode, customFinishInput, library]);

  // ── Athlete profile + fueling (About-you / Fuel steps) ───────────────────
  // ONE shared hook owns physiology, dietary/flavor and the fueling profile
  // (sweat→sodium, gut level, per-sport + race-day targets, the gut ramp),
  // hydration and the profile-first save primitives. The race-day estimate
  // depends on the goal finish + the athlete's physiology, so we feed the goal
  // in here; everything else is self-contained. (`sodiumTarget`,
  // `hasExplicitSodium`, `estimatedRaceCarbs`, `savedRaceDayTarget`,
  // `targetsInPlay` etc. now live on `fuel` — the wizard reads them below.)
  const fuel = useAboutYouFuel({
    fuelEstimatePolicy: library?.fuelEstimatePolicy,
    goalMinutes: band?.goalMinutes ?? goalHours * 60
  });

  // B6 — the live projection tracks the athlete's actual targets and recomputes
  // as they type: an explicit race-day number wins (once the panel's in play);
  // else the per-sport run target they typed; else a saved race-day/run target;
  // else the policy estimate.
  const projectedRaceCarbs =
    fuel.targetsInPlay && fuel.raceDayCarbs > 0
      ? fuel.raceDayCarbs
      : fuel.targetsInPlay && fuel.runCarbs > 0
        ? fuel.runCarbs
        : fuel.savedRaceDayTarget > 0
          ? fuel.savedRaceDayTarget
          : (fuel.estimatedRaceCarbs ?? 65);
  const projectedRaceTotal = Math.round((projectedRaceCarbs * goalHours) / 10) * 10;

  // C12 — recap Fueling row: a g/hr only when the athlete has custom targets in
  // play (locked-in, or tuning with the panel open). Without them, the race-day
  // estimate already shows in the projected-race-day card below, so we don't
  // duplicate it — we reflect their gut-experience answer instead. Per-sport
  // Runs / Rides ride alongside custom targets when a number exists (typed or
  // saved) — 0 = "we pick from your plan". The ramp note rides along in both.
  const gutExperienceSummary =
    fuel.gutLevel === "new"
      ? "This is new to me"
      : fuel.gutLevel === "tuning"
        ? "I'm still fine-tuning"
        : fuel.gutLevel === "locked"
          ? "Locked-in target"
          : null;
  const recapRunTarget = fuel.targetsInPlay
    ? fuel.runCarbs > 0
      ? fuel.runCarbs
      : (fuel.profileView?.profile?.perSportTargets?.run ?? 0)
    : 0;
  const recapRideTarget = fuel.targetsInPlay
    ? fuel.rideCarbs > 0
      ? fuel.rideCarbs
      : (fuel.profileView?.profile?.perSportTargets?.ride ?? 0)
    : 0;
  // Recap "Plan" row — the ONE schedule fact, merging the old Plan + Window
  // rows (both were the same length, both linked to the length step, and the race date
  // now lives in the Event row). Length + start date; flags the start-partway
  // case (race closer than the full plan → fewer fuel weeks than plan weeks).
  const recapPlanSummary =
    journeyStartYmd == null
      ? `${planWeeks} weeks`
      : fuelWeeks < planWeeks
        ? `${fuelWeeks} of ${planWeeks} weeks · starts ${formatYmdShort(journeyStartYmd)}`
        : `${planWeeks} weeks · starts ${formatYmdShort(journeyStartYmd)}`;
  const recapFuelingSummary = [
    fuel.targetsInPlay ? `Race day ${projectedRaceCarbs} g/hr` : gutExperienceSummary,
    recapRunTarget > 0 ? `Runs ${recapRunTarget}` : null,
    recapRideTarget > 0 ? `Rides ${recapRideTarget}` : null,
    fuel.rampEnabled ? "gut ramp on" : "full targets from day 1"
  ]
    .filter(Boolean)
    .join(" · ");

  // ── No-race review (mock 1g) ─────────────────────────────────────────────
  // The training summary chip + guardrail-5 warnings, and the four review
  // rows. Only rendered on the no-race path (the race review keeps its recap).
  const introSummary = useMemo(() => computeIntroSummary(summary ?? null), [summary]);
  // The review's Training row names the answer; null = the question was skipped.
  const connectorName =
    connectorConnected?.label ?? (connectorOffered.length === 1 ? connectorOffered[0]!.label : null);
  const trainingChoiceLabel: string | null = skipMethod
    ? null
    : trainingChoice === "plan"
      ? "Fuel Goods training plan"
      : trainingChoice === "connector"
        ? connectorName
          ? `Connect ${connectorName}`
          : "Connect my training app"
        : trainingChoice === "import"
          ? "Import my coach's plan"
          : trainingChoice === "generate"
            ? "Balanced week"
            : trainingCard === "manual"
              ? "Enter it manually"
              : "Build my own";
  const reviewRows = useMemo(() => {
    const weightNum = fuel.weightText ? Number(fuel.weightText) : NaN;
    const weightKg =
      Number.isFinite(weightNum) && weightNum > 0
        ? Math.round(weightToKg(weightNum, fuel.weightUnit))
        : null;
    const sodiumLabel =
      fuel.sweat === "lightly"
        ? "low sodium"
        : fuel.sweat === "pretzel"
          ? "high sodium"
          : "average sodium";
    return buildReviewRows({
      summaryChip: trainingChoiceLabel
        ? `${trainingChoiceLabel} · ${introSummary.chip}`
        : introSummary.chip,
      weightKg,
      allergenCount: fuel.dietaryNeeds.size,
      sweatUnsure: fuel.sweatUnsure,
      sodiumLabel,
      gutLevel: fuel.gutLevel,
      rampEnabled: fuel.rampEnabled,
      dropWeeks
    });
  }, [
    introSummary.chip,
    trainingChoiceLabel,
    fuel.weightText,
    fuel.weightUnit,
    fuel.dietaryNeeds.size,
    fuel.sweatUnsure,
    fuel.sweat,
    fuel.gutLevel,
    fuel.rampEnabled,
    dropWeeks
  ]);

  // D-16 (architecture C): step metadata + the active path's ordered step ids
  // live in `wizard/wizardMeta.ts` — the ONE source of truth for navigation and
  // the "Step X of N" counter, so the two can never disagree. The review's
  // title/subtitle stay dynamic (plan weeks / build / no race), handled at
  // render below.
  const stepOrder = useMemo(
    () => wizardStepOrder({ hasRace, isPlan: !isBuild, skipMethod }),
    [hasRace, isBuild, skipMethod]
  );
  const stepPos = Math.max(0, stepOrder.indexOf(step));
  const stepMeta = STEP_META[step];
  const analytics = useOnboardingAnalytics({
    entry,
    step,
    stepOrder,
    hasRace,
    trainingChoice,
    skippedMethod: skipMethod,
    csrfToken
  });

  // A multi-distance event needs a distance picked (inline) before we can continue.
  const needsDistanceChoice =
    !!selectedRace && raceDistanceChoices.length > 1 && !selectedRace.distanceLabel;
  // Distances shown under the selected race: the event's full option list when we
  // have it (catalog picks), else the single chosen label (pinned / custom / existing).
  const distanceOptions: CatalogDistanceOption[] =
    raceDistanceChoices.length > 0
      ? raceDistanceChoices
      : selectedRace?.distanceLabel
        ? [{ label: selectedRace.distanceLabel, meters: null }]
        : [];
  const cancelCustomFinish = useCallback(() => {
    setGoalMode("preset");
    setCustomFinishInput("");
    setFinish(finishBeforeCustomRef.current);
  }, []);
  const openCustomFinish = useCallback(() => {
    finishBeforeCustomRef.current = goalMode === "preset" ? finish : null;
    setGoalMode("custom");
    setFinish(null);
  }, [goalMode, finish]);
  const handleCustomFinishBlur = useCallback(() => {
    const finalized = finalizeFinishTimeInput(customFinishInput);
    if (!finalized || parseFinishMinutes(finalized) == null) {
      cancelCustomFinish();
      return;
    }
    if (finalized !== customFinishInput) {
      setCustomFinishInput(finalized);
    }
  }, [customFinishInput, cancelCustomFinish]);
  const handleCustomFinishChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const prevDigits = customFinishInput.replace(/\D/g, "").length;
      const sanitized = sanitizeFinishTimeInput(e.target.value);
      const nextDigits = sanitized.replace(/\D/g, "").length;
      let next = sanitized;
      if (!sanitized.includes(":")) {
        if (nextDigits > prevDigits && nextDigits >= 3) {
          next = autoFormatFinishTime(sanitized);
        }
      } else {
        const [hours = "", mins = ""] = sanitized.split(":", 2);
        next = mins ? `${hours}:${mins.slice(0, 2)}` : hours ? `${hours}:` : "";
      }
      if (isPartialFinishTimeFormat(next)) {
        setCustomFinishInput(next);
      }
    },
    [customFinishInput]
  );
  const customFinishInvalid =
    customFinishInput.length > 0 && parseFinishMinutes(customFinishInput) == null;
  useEffect(() => {
    if (goalMode === "custom") {
      customFinishInputRef.current?.focus();
    }
  }, [goalMode]);
  // One decision per screen (D-16): required screens gate on their one answer.
  // ONE per-step gate — `stepGate` returns the blocking nudge message (null =
  // the step passes), and BOTH the Continue button's enabled state and the
  // gate's validate read it, so the two can never disagree. Every step id is
  // listed explicitly (the switch is exhaustive over `WizardStepId`).
  function stepGate(s: WizardStepId): string | null {
    switch (s) {
      case "race":
        // Wait for the library that matches THIS race's distance before moving
        // on: it decides plan vs no-plan path, and gating here is what removes
        // the "advance, then the screen flips" behaviour on a distance switch.
        if (!selectedRace) return "Pick a race from search or add one to continue.";
        if (raceInPast) return "That race date has passed — choose an upcoming event.";
        if (needsDistanceChoice) return "Pick your distance to continue.";
        if (!libraryReady) return loadError ? LIBRARY_ERROR_NUDGE : "Checking plans…";
        // No blocking when the distance has no plans — the user skips straight
        // to the About-you step and adds their own workouts.
        return null;
      case "goal":
        if (goalMode === "just_finish") return null;
        if (goalMode === "custom") {
          return parseFinishMinutes(customFinishInput) == null
            ? "Enter a finish time (H:MM) to continue."
            : null;
        }
        return finish ? null : "Pick a goal finish time to continue.";
      case "length":
        // No-plan distances never reach the length step — they skip ahead.
        return weeks ? null : "Pick a plan length to continue.";
      case "fuel":
        return fuel.gutLevel ? null : "Tell us how much fuel practice you have to continue.";
      case "q1": // Q1 — one-tap answers, never blocks (D-16).
      case "method": // Training method — cards auto-advance; no Continue.
      case "body": // About your body — encouraged, never blocks (D-16).
      case "needs": // Needs & tastes — encouraged, never blocks (D-16).
      case "cadence": // Box cadence — "Every 2 weeks" preselected (D4).
      case "review": // Review — the finish buttons own their own gating.
        return null;
    }
  }
  const canContinue = stepGate(step) == null;

  // The no-race path has no race/plan, so it never waits on the plan library
  // (which only loads once Q1 says there is a race).
  const gateReady = libraryReady || noRace;
  const continueGate = useGatedAction({
    canProceed: canContinue && gateReady,
    validate: () =>
      stepGate(step) ??
      (gateReady ? null : loadError ? LIBRARY_ERROR_NUDGE : "Finding training plans..."),
    // Advance along the active path — the no-plan / no-race skips are baked
    // into `stepOrder`, so there's no per-step skip logic to keep in sync here.
    onProceed: () => setStep(stepOrder[Math.min(stepOrder.length - 1, stepPos + 1)]),
    autoClearWhen: canContinue && gateReady
  });

  const applyGate = useGatedAction({
    canProceed: isBuild || !!plan,
    busy: applying,
    validate: () =>
      (!isBuild && !plan) || !selectedRace
        ? "Plan preview isn't ready yet — check your goal settings and try again."
        : null,
    onProceed: () => void handleApply(),
    autoClearWhen: isBuild || !!plan
  });

  // "Add an event" link → our standard Add-event form (with our date picker).
  // On save it creates a real upcoming event in the DB, which becomes the
  // selected race (so the date comes from the event, not a separate field).
  type AddEventPrefill = {
    name?: string;
    eventDate?: string;
    initialLocation?: string;
    initialDistanceUnset?: boolean;
  };

  function openAddEvent(presetOrOpts: string | AddEventPrefill) {
    const opts: AddEventPrefill =
      typeof presetOrOpts === "string" ? { name: presetOrOpts } : presetOrOpts;
    const id = openModal(
      <AddEventModal
        initialName={opts.name ?? ""}
        initialEventDate={opts.eventDate ?? ""}
        initialLocation={opts.initialLocation ?? ""}
        initialDistanceUnset={opts.initialDistanceUnset}
        minDate={today}
        onClose={() => closeModal(id)}
        onSave={async (input: CreateCustomEventInput) => {
          const keepCatalogDistances = opts.initialDistanceUnset === true;
          if (isCustomer) {
            const { event } = await createEvent(input);
            const dateYmd = catalogEventStartToYmd(event.eventDate);
            const distanceLabel = event.distanceLabel ?? undefined;
            setSelectedRace({
              name: event.name,
              dateYmd,
              location: formatEventLocation(event) || undefined,
              customerEventId: event.id,
              distanceLabel
            });
            if (keepCatalogDistances) {
              setRaceDistanceChoices((prev) => {
                if (!distanceLabel) return prev;
                if (prev.some((d) => d.label === distanceLabel)) return prev;
                return [...prev, { label: distanceLabel, meters: event.distanceMeters ?? null }];
              });
            } else {
              setRaceDistanceChoices([]);
            }
            setQuery("");
          } else {
            // Anon can't persist a CustomerEvent — capture the race locally. It's
            // created on login via the pending-race replay (held when applied).
            const distanceLabel = input.distance ? eventDistanceLabel(input.distance) : undefined;
            setSelectedRace({
              name: input.name,
              dateYmd: input.eventDate,
              location:
                [input.locationCity, input.locationRegion].filter(Boolean).join(", ") || undefined,
              distanceLabel
            });
            if (keepCatalogDistances) {
              setRaceDistanceChoices((prev) => {
                if (!distanceLabel) return prev;
                if (prev.some((d) => d.label === distanceLabel)) return prev;
                return [...prev, { label: distanceLabel, meters: null }];
              });
            } else {
              setRaceDistanceChoices([]);
            }
            setQuery("");
          }
          closeModal(id);
        }}
      />
    );
  }

  // Same-event guard (event-scoped-race-plans B3/D4): a SAME-EVENT re-apply
  // replaces the existing race plan behind an explicit confirm (the server
  // archives it either way). DIFFERENT-event overlaps are the server's job
  // now (J1): create 409s `journey_overlap` and the funnel's unified
  // RoleResolutionSheet (chain/split/nest) takes over via
  // useEventSetup.createRaceJourney.
  function handleApply() {
    if ((!isBuild && !plan) || !selectedRace) return;
    const race = selectedRace;
    // Match on RACE DAY, not event identity: the server keys same-race
    // replacement on `race:${raceYmd}` (journeys/raceDate.ts), so a different
    // race on the same day is still archived-and-replaced by the create. The
    // confirm must therefore fire for any active plan sharing the date, or
    // the other plan's upcoming sessions would be removed without consent.
    const sameEvent = activeRacePlans.find((j) => j.raceYmd === race.dateYmd);
    if (!sameEvent) {
      void doApply();
      return;
    }
    const other = journeyRaceName(sameEvent);
    const id = openModal(
      <ModalShell
        eyebrow="Replace race plan?"
        title={`Replace your ${other} plan?`}
        onClose={() => closeModal(id)}
        width={480}
        footer={
          <>
            <span />
            <div className="fgm-footer-actions">
              <button
                type="button"
                className="fg-btn fg-btn--ghost fg-btn--sm"
                onClick={() => closeModal(id)}>
                Cancel
              </button>
              <button
                type="button"
                className="fg-btn fg-btn--primary fg-btn--sm"
                onClick={() => {
                  closeModal(id);
                  void doApply();
                }}>
                Replace &amp; apply
              </button>
            </div>
          </>
        }>
        <div className="fgp-modalshell__pad fgf-removecopy">
          You already have an active race plan for this race day
          {sameEvent.raceYmd ? (
            <>
              {" "}
              (<strong>{formatYmdLong(sameEvent.raceYmd)}</strong>)
            </>
          ) : null}
          . Applying this one replaces it — the old plan&apos;s upcoming sessions come off your
          calendar so nothing counts twice. Past weeks, boxes you&apos;ve already ordered (their
          workouts and fuel are kept as bought), and workouts you added yourself stay put.
        </div>
      </ModalShell>
    );
  }

  // ── Milestone short-circuit (journeys overhaul P2, J4/D1) ────────────────
  // A picked race whose date falls INSIDE an active build's window is likely a
  // tune-up, not a second goal. Step 0 offers the exit; confirming creates the
  // event with `intent: "milestone"` (no journey — full race-day-kit
  // generation is a later phase, P5) and closes the wizard.
  const containingJourney = useMemo(
    () => (selectedRace ? journeyCoveringDate(activeRacePlans, selectedRace.dateYmd) : null),
    [selectedRace, activeRacePlans]
  );

  /**
   * T9 fix (spec §9.1/§9.4, SF21): "Add as a tune-up" must actually PERSIST the
   * role. For an ALREADY-EXISTING customer event the old funnel short-circuit
   * no-op'd (no create, no reclassify) yet still fired a success toast — a
   * silent lie. Now:
   *   - existing event (`customerEventId` set, customer) → `reclassifyEvent`
   *     (S2's `POST /:id/role`) which archives any own-journey, strips workouts,
   *     stamps `intent:"milestone"`. Toast ONLY after it resolves.
   *   - new event / anon → the `setupEvent` funnel with `forcedIntent:"milestone"`
   *     creates the event (or anon draft) already at intent milestone.
   * Either way the toast fires only on real success (SF21); a failure surfaces
   * an error toast and keeps the wizard open to retry.
   */
  async function addAsMilestone(): Promise<void> {
    if (!selectedRace || !containingJourney) return;
    const race = selectedRace;
    const existingEventId = race.customerEventId;
    try {
      if (isCustomer && existingEventId) {
        // Existing event: persist the flip through the sanctioned reclassify
        // route (into the containing build so the server nests it there).
        await reclassify(existingEventId, "milestone", { intoJourneyId: containingJourney.id });
      } else {
        const needsCreate = isCustomer && !existingEventId;
        await setupEvent({
          race: {
            name: race.name,
            dateYmd: race.dateYmd,
            customerEventId: race.customerEventId,
            eventCatalogId: race.eventCatalogId,
            distanceLabel: race.distanceLabel
          },
          journeys: activeRacePlans,
          forcedIntent: "milestone",
          createEvent: needsCreate
            ? async (intent) => {
                const input = race.eventCatalogId
                  ? {
                      eventCatalogId: race.eventCatalogId,
                      distanceLabel: race.distanceLabel,
                      intent
                    }
                  : { name: race.name, eventDate: race.dateYmd, intent };
                return (await createEvent(input)).event;
              }
            : undefined
        });
      }
    } catch (err) {
      // Honest failure (SF21): no success toast, surface the error, stay open.
      pushToast({
        tone: "error",
        message: err instanceof Error ? err.message : "Couldn't add the prep race. Try again.",
        durationMs: 8000
      });
      throw err;
    }
    pushToast({
      tone: "success",
      message: `${race.name} added as a prep race inside your ${journeyRaceName(containingJourney)} training plan.`,
      durationMs: 8000
    });
    analytics.markFinished();
    onClose();
  }

  /**
   * "Add as a tune-up instead" (spec §9.1, C2): a picked race whose date sits
   * inside an active build collides with it. Rather than the bespoke
   * `MilestoneConfirmSheet`, open the ONE `RoleResolutionSheet` (`pair` kind)
   * driven by the shared geometry — the new (earlier) race offers `nest_new`
   * ("it's a tune-up") plus any valid chain/split. Picking `nest_new` persists
   * via `addAsMilestone` (T9); chain/split keep it a goal and hand back to the
   * wizard's normal apply (the user can then Continue).
   */
  function openMilestoneConfirm() {
    if (!selectedRace || !containingJourney) return;
    const race = selectedRace;
    const containing = containingJourney;
    const exRaceYmd = containing.raceYmd;
    const exStartYmd = containing.planStartYmd ?? containing.drops[0]?.startYmd ?? exRaceYmd;
    const newStartYmd = defaultJourneyWindow({
      raceYmd: race.dateYmd,
      todayYmd: today
    }).startYmd;
    const context =
      exRaceYmd && exStartYmd
        ? (() => {
            const newEvent: EventRef = {
              id: race.customerEventId ?? "new-event",
              name: race.name,
              ymd: race.dateYmd,
              hasJourney: false
            };
            const existingEvent: EventRef = {
              id: containing.customerEventId ?? containing.id,
              name: journeyRaceName(containing),
              ymd: exRaceYmd,
              hasJourney: true
            };
            const result = evaluateCollision(newEvent, existingEvent, {
              newStartYmd,
              newRaceYmd: race.dateYmd,
              exStartYmd,
              exRaceYmd
            });
            if (!result.collides) return null;
            const anchor = result.anchorId === newEvent.id ? newEvent : existingEvent;
            return {
              kind: "pair" as const,
              events: [newEvent, existingEvent] as [EventRef, EventRef],
              anchor,
              outcomes: result.outcomes
            };
          })()
        : null;

    // Geometry couldn't produce a pair (shouldn't happen for an in-window date)
    // — fall back to the direct milestone persist so the button still works.
    if (!context) {
      void addAsMilestone();
      return;
    }

    const id = openModal(
      <RoleResolutionSheet
        context={context}
        onResolve={(choices) => {
          closeModal(id);
          // Only ever a `pair` sheet here → a 1-element batch.
          const choice = choices[0];
          if (choice?.kind !== "pair") return;
          // nest_new → the picked race is a prep race (persist via T9's path).
          if (choice.outcome === "nest_new") {
            void addAsMilestone();
            return;
          }
          // remove_existing / remove_existing_event (B3 + Part 2) are
          // funnel-only — this pre-check sheet never passes
          // `offerRemoveExisting`, so they can't arrive; narrow them out.
          if (choice.outcome === "remove_existing") return;
          if (choice.outcome === "remove_existing_event") return;
          // chain/nest_existing keep it a goal. Hold the answer so the
          // apply carries it as `overlapResolution` — the server resolves the
          // overlap in one shot instead of 409ing and re-asking the same
          // question the user just answered (B2, robustness spec §5.5).
          setPendingOverlapMode(choice.outcome === "nest_existing" ? "demote" : choice.outcome);
        }}
        onCancel={() => closeModal(id)}
      />
    );
  }

  // Q1 (D-16) — the two answers advance on tap (no Continue).
  function pickRacePath() {
    setHasRace(true);
    setFrozenSkipMethod(liveSkipMethod);
    continueGate.clearNudge();
    setStep("race");
  }
  function pickNoRace() {
    // Backstop: there is only ever ONE rolling journey. Surfaces guard their
    // own doors; this covers every other way into the wizard.
    if (activeRacePlans.some((j) => j.journeyType === "rolling")) {
      pushToast({
        tone: "info",
        message: "You're already fueling your training",
        detail: "Your rolling fuel boxes are below."
      });
      onClose();
      navigate("/my-fuel");
      return;
    }
    setHasRace(false);
    setFrozenSkipMethod(liveSkipMethod);
    setSelectedRace(null);
    setRaceDistanceChoices([]);
    setQuery("");
    continueGate.clearNudge();
    setStep(liveSkipMethod ? "fuel" : "method");
  }

  // Back walks the SAME active path as Continue (`stepOrder`), so the no-plan /
  // no-race skips are handled without a second copy of the skip rules.
  function handleBack() {
    if (stepPos <= 0) {
      onBack?.();
      return;
    }
    setStep(stepOrder[stepPos - 1]);
  }

  async function doApply() {
    if (submitLockRef.current) return;
    if ((!isBuild && !plan) || !selectedRace) return;
    submitLockRef.current = true;
    setApplying(true);
    setApplyError(null);
    setOverlapUnresolved(false);
    try {
      await fuel.saveAboutYou();
      // Custom targets apply when the panel was in play (locked-in default, or
      // opened via the "enter your own targets" link). 0 = unset. Typed values
      // are clamped to the UI band — the number-input min/max only guide the
      // spinner, and an out-of-band value would 400 opaquely (audit C5).
      const clampCarb = clampCarbTarget;
      const targetsActive = fuel.targetsInPlay;
      // EVENT-scoped: only the race-day anchor rides the journey (D2).
      const customTargets =
        targetsActive && clampCarb(fuel.raceDayCarbs) > 0
          ? { raceDayGramsPerHour: clampCarb(fuel.raceDayCarbs) }
          : undefined;
      // ATHLETE-state → fueling-profile patch, written FIRST by the handler (R4).
      const fuelingProfile = fuel.buildFuelingProfilePatch();
      const applyResult = await onApply({
        // No-plan path sends no planId (a stale plan from a prior distance must
        // never leak in) → the apply creates a plan-less "build" journey.
        method: isBuild ? "build" : "fuel_goods_plan",
        trainingChoice,
        connectorLinked: connectorConnected != null,
        connectorOfferLabels: connectorOffered.map((c) => c.label),
        hasUpcomingWorkouts: knownUpcoming,
        planId: isBuild ? undefined : effectivePlanId,
        raceDate: selectedRace.dateYmd,
        raceName: selectedRace.name,
        eventCatalogId: selectedRace.eventCatalogId,
        distanceLabel: selectedRace.distanceLabel,
        customerEventId: selectedRace.customerEventId,
        planTypeLabel: library?.distances.find((d) => d.id === distance)?.label,
        dropWeeks,
        customTargets,
        fuelingProfile,
        // J2 window start from the step-2 selection (library plan or custom
        // variant), clamped to today for mid-plan applies.
        startYmd: journeyStartYmd ?? undefined,
        // The pre-check sheet's chain/split/demote answer (B2) — the server
        // resolves the overlap on the create instead of re-asking via 409.
        overlapResolution: pendingOverlapMode ? { mode: pendingOverlapMode } : undefined
      });
      // `false` = the user cancelled a nested step (overlap-resolution sheet):
      // keep the wizard open so a dismiss never dumps them back onto the screen
      // that launched it, and surface a hint that the overlap must be resolved.
      // Any other resolution (success) closes as before.
      if (applyResult === false) {
        setOverlapUnresolved(true);
        releaseSubmit();
        return;
      }
      analytics.markFinished();
      onClose();
    } catch (err) {
      if (!mountedRef.current) return;
      setApplyError(err instanceof Error ? err.message : "Couldn't apply the plan. Try again.");
      releaseSubmit();
    }
  }

  // No-race finish: persist the profile (same profile-first order as the apply
  // path), then create the ONE rolling journey with the chosen cadence +
  // coverage and hand the outcome to the host (toast + handoff + landing).
  async function doStartRolling() {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setApplying(true);
    setApplyError(null);
    try {
      await fuel.saveAboutYou();
      if (csrfToken)
        await patchFuelingProfile(fuel.buildFuelingProfilePatch(), csrfToken, refreshCsrf);
      const { journey } = await createRollingJourney({ dropWeeks, coverageWeeks });
      analytics.markFinished();
      onRollingCreated({
        kind: "rolling",
        journey: {
          id: journey.id,
          planGroupId: journey.planGroupId,
          raceName: null,
          raceYmd: null,
          startYmd: journey.startYmd
        },
        trainingChoice,
        connectorLinked: connectorConnected != null,
        connectorOfferLabels: connectorOffered.map((c) => c.label),
        hasUpcomingWorkouts: knownUpcoming
      });
      if (!mountedRef.current) return;
      onClose();
    } catch (err) {
      if (!mountedRef.current) return;
      setApplyError(
        err instanceof Error ? err.message : "Couldn't start your fuel boxes. Try again."
      );
      releaseSubmit();
    }
  }

  return (
    <ModalShell
      eyebrow={
        <span className="fgp-wz-stepbrow">
          <LucideIcon name={stepMeta.icon} size={13} color="var(--fg-purple-deep)" />
          Step {stepPos + 1} of {stepOrder.length} · {stepMeta.label}
        </span>
      }
      title={
        step === "review" && noRace
          ? "Review and build your first box"
          : step === "review" && isBuild
            ? "Your race journey"
            : step === "review"
              ? `Your ${planWeeks}-week journey`
              : stepMeta.title
      }
      subtitle={
        step === "review" && noRace
          ? "Check everything below, then we'll build your first box's recommendations."
          : stepMeta.subtitle
      }
      progress={(stepPos + 1) / stepOrder.length}
      scrollResetKey={step}
      onClose={onClose}
      onBack={stepPos === 0 ? onBack : handleBack}
      tallSheet={isMobile}
      // D-06 — ONE shell width for every step (no per-step jumps).
      width={680}
      footerSpread
      footer={
        <>
          <span className="fgp-modal-footnote">
            {step === "review"
              ? noRace
                ? "Buy each box as you go — nothing upfront. Add sessions any time and we size them."
                : isBuild
                  ? "Adds your race — add your own workouts and we'll build fuel boxes around them."
                  : `Drops ${planWeeks} weeks onto your calendar, ending on race day. Edit any session after.`
              : (STEP_FOOTNOTES[step] ?? null)}
          </span>
          {/* Q1 and the method step are one-tap card screens — footnote only. */}
          {step === "q1" || step === "method" ? null : stepPos < stepOrder.length - 1 ? (
            <div className="fgp-modal-footactions">
              {/* D13 — the optional steps can be skipped; untouched fields are never saved. */}
              {(step === "body" || step === "needs") && (
                <button
                  type="button"
                  className="fg-btn fg-btn--ghost fg-btn--sm"
                  onClick={() => {
                    continueGate.clearNudge();
                    setStep(stepOrder[stepPos + 1]);
                  }}>
                  Skip
                </button>
              )}
              <button
                type="button"
                className={`fg-btn fg-btn--primary fg-btn--sm${continueGate.gateBtnClass}`}
                aria-disabled={continueGate.ariaDisabled}
                disabled={continueGate.disabled}
                onClick={continueGate.handleClick}
                aria-busy={checkingPlans && step === "race" ? true : undefined}>
                {step === "length" && weeks
                  ? `Continue with ${planWeeks} weeks →`
                  : checkingPlans && step === "race"
                    ? "Checking plans…"
                    : "Continue →"}
              </button>
            </div>
          ) : noRace ? (
            <button
              type="button"
              className="fg-btn fg-btn--lime fg-btn--sm"
              disabled={applying}
              onClick={() => void doStartRolling()}>
              {applying ? "Building…" : "Build my first box →"}
            </button>
          ) : (
            <div className="fgp-modal-footactions">
              {applyGate.nudge && <ModalFootVal>{applyGate.nudge}</ModalFootVal>}
              <button
                type="button"
                className={`fg-btn fg-btn--lime fg-btn--sm${applyGate.gateBtnClass}`}
                aria-disabled={applyGate.ariaDisabled}
                disabled={applyGate.disabled}
                onClick={applyGate.handleClick}>
                {applying ? "Applying…" : "Put it on my calendar →"}
              </button>
            </div>
          )}
        </>
      }>
      <div className="fgp-modalshell__pad">

        {/* Only the race path uses the library — never Q1 or the no-race path. */}
        {loadError && step !== "q1" && !noRace && (
          <div className="fgp-modal-callout fgp-modal-callout--error" style={{ marginTop: 16 }}>
            <span>{loadError}</span>{" "}
            <button type="button" className="fgp-wz-ramplink" onClick={retryLibrary}>
              Retry
            </button>
          </div>
        )}
        {/* The no-race path never waits on the plan library. */}
        {step !== "q1" && step !== "race" && !libraryReady && !loadError && !noRace && (
          <ModalLoadingState
            accessibilityLabel="Loading training plans"
            message="Loading training plans…"
          />
        )}

        {/* ── Q1 · "Do you have a race coming up?" — no Continue (D-16) ── */}
        {step === "q1" && <IntentStep onPickRace={pickRacePath} onPickNoRace={pickNoRace} />}

        {step === "race" && (
          <RaceStep
            step={step}
            existingJourneyWithWorkouts={existingJourneyWithWorkouts}
            navigate={navigate}
            onClose={onClose}
            selectedRace={selectedRace}
            raceInPast={raceInPast}
            weeksOut={weeksOut}
            raceDate={raceDate}
            raceSearchRef={raceSearchRef}
            raceFieldId={raceFieldId}
            focusRaceSearchRef={focusRaceSearchRef}
            query={query}
            setQuery={setQuery}
            setSelectedRace={setSelectedRace}
            setRaceDistanceChoices={setRaceDistanceChoices}
            continueGate={continueGate}
            distanceOptions={distanceOptions}
            noPlansForDistance={noPlansForDistance}
            openAddEvent={openAddEvent}
            otherRaces={otherRaces}
            today={today}
            containingJourney={containingJourney}
            openMilestoneConfirm={openMilestoneConfirm}
          />
        )}

        {/* ── METHOD — race card + one-tap method cards (D-16/D-19) ── */}
        {step === "method" && (
          <MethodStep
            noRace={noRace}
            selectedRace={selectedRace}
            weeksOut={weeksOut}
            raceDate={raceDate}
            noPlansForDistance={noPlansForDistance}
            card={trainingCard}
            setCard={setTrainingCard}
            onPick={(card) => {
              // D-16 auto-advance: the plan continues to Goal; every other
              // answer is plan-less → Fueling experience (the host runs the
              // connect / import / generate handoff after the finish).
              setTrainingCard(card);
              continueGate.clearNudge();
              setStep(card === "plan" ? "goal" : "fuel");
            }}
            jumpToRace={() => {
              continueGate.clearNudge();
              setStep("race");
            }}
            connectorOffered={connectorOffered}
            connectorConnected={connectorConnected}
            importOffered={importOffered}
            connectorRequiresLogin={!isCustomer}
          />
        )}

        {libraryReady && step === "goal" && (
          <GoalStep
            goalEdit={goalEdit}
            library={library}
            goalMode={goalMode}
            finish={finish}
            setGoalMode={setGoalMode}
            setFinish={setFinish}
            setCustomFinishInput={setCustomFinishInput}
            customFinishInput={customFinishInput}
            customFinishInputRef={customFinishInputRef}
            customFinishInvalid={customFinishInvalid}
            handleCustomFinishChange={handleCustomFinishChange}
            handleCustomFinishBlur={handleCustomFinishBlur}
            cancelCustomFinish={cancelCustomFinish}
            openCustomFinish={openCustomFinish}
            band={band}
            nudge={continueGate.nudge}
          />
        )}

        {/* ── PLAN LENGTH — DB lengths + phase bars + honest start-partway.
             No-plan distances never reach this step (they skip ahead). ── */}
        {libraryReady && step === "length" && (
          <PlanLengthStep
            selectedRace={selectedRace}
            weeksOut={weeksOut}
            raceDate={raceDate}
            library={library}
            sortedLengths={sortedLengths}
            weeks={weeks}
            setWeeks={setWeeks}
            planFinish={planFinish}
            fitWeeksOut={fitWeeksOut}
            recommendedLen={recommendedLen}
            today={today}
            isMobile={isMobile}
            goalMode={goalMode}
            plan={plan}
            finish={finish}
            goalLabel={goalLabel}
            band={band}
            jumpToGoal={() => {
              continueGate.clearNudge();
              setStep("goal");
            }}
            nudge={continueGate.nudge}
          />
        )}

        {/* ── FUELING EXPERIENCE — gut + targets + ramp (D-16) ── */}
        {(library || noRace) && step === "fuel" && (
          <div className="fgp-wizard-panel">
            <FuelExperienceStep
              fuel={fuel}
              // No race → no race day → hide the race-day carb field (gate variant).
              variant={noRace ? "gate" : "wizard"}
              rampStartHint={
                startYmd && compareYmd(startYmd, today) > 0
                  ? "Gut training starts now — ahead of your plan's first week."
                  : undefined
              }
            />
            <ModalValHint message={continueGate.nudge} />
          </div>
        )}

        {/* ── ABOUT YOUR BODY — sex/weight/sweat, never gates ── */}
        {(library || noRace) && step === "body" && (
          <div className="fgp-wizard-panel">
            <BodyStep fuel={fuel} />
          </div>
        )}

        {/* ── NEEDS & TASTES — allergens + flavors, never gates ── */}
        {(library || noRace) && step === "needs" && (
          <div className="fgp-wizard-panel">
            <NeedsTastesStep fuel={fuel} />
          </div>
        )}

        {/* ── BOX CADENCE — the one box decision (gated, D-11) ── */}
        {(library || noRace) && step === "cadence" && (
          <BoxCadenceStep
            dropWeeks={dropWeeks}
            setDropWeeks={setDropWeeks}
            rolling={noRace}
            coverageWeeks={coverageWeeks}
            setCoverageWeeks={setCoverageWeeks}
            coverageChoices={COVERAGE_CHOICES}
            nudge={continueGate.nudge}
          />
        )}

        {step === "review" && (
          <ReviewStep
            noRace={noRace}
            reviewRows={reviewRows}
            trainingWarnings={introSummary.warnings}
            setStep={setStep}
            library={library}
            isBuild={isBuild}
            trainingChoice={trainingChoice}
            noPlansForDistance={noPlansForDistance}
            trainingMethodLabel={trainingChoiceLabel}
            plan={plan}
            dropWindows={dropWindows}
            dropWeeks={dropWeeks}
            selectedRace={selectedRace}
            goalLabel={goalLabel}
            recapPlanSummary={recapPlanSummary}
            recapFuelingSummary={recapFuelingSummary}
            projectedRaceCarbs={projectedRaceCarbs}
            projectedRaceTotal={projectedRaceTotal}
            fuel={fuel}
            applyError={applyError}
            overlapUnresolved={overlapUnresolved}
          />
        )}
      </div>
    </ModalShell>
  );
}
